import React, { createContext, useCallback, useMemo, useState } from 'react'

import { Modal } from 'antd'
import { useSelector } from 'react-redux'
import { isArray, isFunction } from 'lodash'

import {
  acceptInternshipRequest,
  addInternshipRequestFile,
  createInternshipRequest,
  createInternshipRequestPeriod,
  deleteInternshipRequestPeriods,
  setInternshipRequestToOnHold,
  updateInternshipRequest,
  updateInternshipRequestPeriod
} from '../../../utils/api/internshipRequest'
import { selectTranslate } from '../../../reducers/LocaleReducer/LocalReducer'
import { selectUser } from '../../../reducers/UserReducer'
import { onError, onSuccess, resolvePromises } from '../../../utils/apiHelper'
import { RequestPeriod } from '../../../utils/entities/requestPeriod'
import IndividualInternshipRequestForm from './IndividualInternshipRequestForm'
import StudentForm from '../StudentForm/StudentForm'
import { Permissions } from '../StudentForm/SearchStudentModalProvider'
import { InternshipRequest } from '../../../utils/entities/internshipRequest'
import { isObject } from '../../../utils'
import AcceptIndividualInternshipRequestForm from './AcceptIndividualInternshipRequestForm'
import useScreenSize from '../../../hooks/useScreen'
import useDebounce from '../../../hooks/useDebounce'
import useRequest from '../../../hooks/useRequest'
import { getAllInternshipRequestMetadata } from '../../../utils/api/internshipRequestMetadata'
import MetadataFields from '../../../Views/institution/InternshipsView/InternshipsRequestsView/MetadataFields'

export const IndividualInternshipRequestModalContext = createContext()

/** @enum {string} */
export const ModalTypes = Object.freeze({
  FORM: 'FORM',
  DISPLAY_STUDENT: 'DISPLAY_STUDENT',
  DISPLAY_METADATA: 'DISPLAY_METADATA',
  ACCEPT: 'ACCEPT',
  INSTITUTION_VALIDATION: 'INSTITUTION_VALIDATION',
  SECTOR_VALIDATION: 'SECTOR_VALIDATION',
  STUDENT: 'STUDENT'
})

const DEFAULT_REQUEST = { data: null, callback: null, type: ModalTypes.FORM }

const IndividualInternshipRequestModal = ({ children, institutions, schools = { data: [], loading: true }, sectors, schoolSections }) => {
  const t = useSelector(selectTranslate)
  const user = useSelector(selectUser)
  const { width } = useScreenSize()

  const [handler, setHandler] = useState(DEFAULT_REQUEST)
  const [selectedPeriods, setSelectedPeriods] = useState([])

  const displayForm = useMemo(() => !!handler.data && handler.type === ModalTypes.FORM, [handler])
  const displayStudentData = useMemo(() => !!handler.data && handler.type === ModalTypes.DISPLAY_STUDENT, [handler])
  const displayAcceptModal = useMemo(() => !!handler.data && handler.type === ModalTypes.ACCEPT, [handler])
  const displayMetadata = useMemo(() => !!handler.data && handler.type === ModalTypes.DISPLAY_METADATA, [handler])

  const closeModal = useCallback(() => setHandler(DEFAULT_REQUEST), [])

  const submit = useCallback((request, files) => {
    const promise = request.id < 0 ? { call: createInternshipRequest, action: 'create' } : { call: updateInternshipRequest, action: 'update' }
    const oldRequest = handler.data
    const requestPeriodsToCreate = []
    const requestPeriodsToUpdate = []
    const requestPeriodsToDelete = []

    if (promise.action === 'update') {
      request.requestPeriods.forEach(p => {
        if (p.id < 0) {
          requestPeriodsToCreate.push(new RequestPeriod(p))
        } else {
          const oldPeriod = oldRequest.requestPeriods.find(rp => rp.id === p.id)

          if (oldPeriod && (oldPeriod.startDate !== p.startDate || oldPeriod.endDate !== p.endDate)) {
            requestPeriodsToUpdate.push(new RequestPeriod(p))
          }
        }
      })

      oldRequest.requestPeriods.forEach(p => {
        if (!request.requestPeriods.find(rp => rp.id === p.id)) {
          requestPeriodsToDelete.push(p)
        }
      })
      request.requestPeriods = []
    }

    promise.call(user, request.toRequestBody()).then(json => {
      if (json?.data) {
        const promisesStack = []

        requestPeriodsToCreate.forEach(rp => {
          promisesStack.push(createInternshipRequestPeriod(user, oldRequest, rp.toRequestBody()))
        })
        requestPeriodsToUpdate.forEach(rp => {
          promisesStack.push(updateInternshipRequestPeriod(user, oldRequest, rp.toRequestBody()))
        })

        if (requestPeriodsToDelete.length > 0) {
          promisesStack.push(deleteInternshipRequestPeriods(user, oldRequest, { ids: requestPeriodsToDelete.map(rp => rp.id) }))
        }

        if (isArray(files)) {
          files.forEach(f => {
            promisesStack.push(addInternshipRequestFile(user, json.data, f))
          })
        }

        resolvePromises(promisesStack).then(() => {
          onSuccess(t('internship_request.' + promise.action + '.success'))

          if (handler.callback) {
            handler.callback(json.data)
          }
        })
      }
    }).catch(() => {
      onError(t('internship_request.' + promise.action + '.error'))
    }).finally(() => {
      setHandler(DEFAULT_REQUEST)
    })
  }, [handler, user, t])

  const handleSubmit = useDebounce(submit, 200)

  const setRequestAsPending = useCallback(() => {
    setInternshipRequestToOnHold(user, handler.data).then(json => {
      if (json?.data) {
        if (isFunction(handler.callback)) {
          handler.callback(json.data)
        }

        onSuccess(t('internship_request.set_pending.success'))
        setHandler(DEFAULT_REQUEST)
      } else {
        onError(t('internship_request.set_pending.error'))
      }
    })
  }, [handler, user, setHandler, t])

  const acceptRequest = useCallback(() => {
    acceptInternshipRequest(user, handler.data, selectedPeriods).then(json => {
      if (json?.data) {
        if (isFunction(handler.callback)) {
          handler.callback(json.data)
        }

        onSuccess(t('internship_request.actions.accept.success'))
        setHandler(DEFAULT_REQUEST)
        setSelectedPeriods([])
      } else {
        onError(t('internship_request.actions.accept.error'))
      }
    })
  }, [user, handler, selectedPeriods, t, setSelectedPeriods, setHandler])

  const student = useMemo(() => handler.data ? new InternshipRequest(handler.data).getStudent() : null, [handler.data])

  const parameters = useMemo(() => ({ }), [user.id])

  const metadataFields = useRequest({
    request: getAllInternshipRequestMetadata,
    parameters: parameters
  })

  return (
    <>
      <IndividualInternshipRequestModalContext.Provider value={{ setHandler: setHandler }}>
        {children}
      </IndividualInternshipRequestModalContext.Provider>
      <Modal
        width={width > 1024 ? 1024 : '90%'}
        title={t('internship_request_modal.title')}
        visible={displayForm}
        onCancel={closeModal}
        footer={null}
        destroyOnClose
      >
        <IndividualInternshipRequestForm
          data={displayForm ? handler.data : null}
          schools={schools}
          schoolSections={schoolSections}
          sectors={sectors}
          institutions={institutions}
          onSubmit={handleSubmit}
          metadataFields={metadataFields.data}
        />
      </Modal>
      <Modal
        title={t('student_information_modal.title')}
        visible={displayStudentData}
        onCancel={closeModal}
        onOk={setRequestAsPending}
        okText={t('student_information_modal.ok_button')}
        cancelText={t('student_information_modal.cancel_button')}
      >
        {displayStudentData && (
          <StudentForm
            data={student}
            permission={Permissions.READ_ONLY}
          />
        )}
      </Modal>
      <Modal
        title={t('metadata_modal.title')}
        visible={displayMetadata}
        onCancel={closeModal}
        onOk={setRequestAsPending}
        okText={t('metadata_modal.ok_button')}
        cancelText={t('metadata_modal.cancel_button')}
      >
        {displayMetadata && (
          <MetadataFields
            metadataFields={metadataFields.data}
            disabled
            values={handler.data.metadata}
          />
        )}
      </Modal>
      <Modal
        title={t('accept_internship_request_modal.title')}
        visible={displayAcceptModal}
        onCancel={closeModal}
        onOk={acceptRequest}
        okText={t('student_information_modal.accept_internship_request_button')}
        okButtonProps={{ disabled: selectedPeriods.length === 0 || !isObject(handler.data?.sector) }}
        cancelText={t('student_information_modal.cancel_button')}
      >
        {displayAcceptModal && (
          <AcceptIndividualInternshipRequestForm
            sectors={sectors}
            student={student}
            request={handler.data}
            onSuccess={onSuccess}
            onError={onError}
            onSectorSelect={value => setHandler(prev => ({ ...prev, data: { ...prev.data, sector: { id: value } } }))}
            onPeriodsSelect={setSelectedPeriods}
          />
        )}
      </Modal>
    </>
  )
}

export default IndividualInternshipRequestModal
